import { useEffect, useId, useMemo, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Combobox } from '@/components/ui/Combobox';
import { Dialog } from '@/components/ui/Dialog';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import {
  VPN_PROFIL_FEHLER,
  VPN_PROFIL_MUSTER,
} from '@/components/customers/CustomerFormDialog';
import { Input } from '@/components/ui/Input';
import { useCustomers, useCustomerLocations } from '@/hooks/useCustomers';
import {
  fqdnVorschlaege,
  osVersionVorschlaege,
  vorschlagsWerte,
  zabbixHostLabel,
  zabbixUebernahme,
} from '@/components/systems/systemVorschlaege';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import {
  useCreateSystem,
  useSystemFacets,
  useSystems,
  useUpdateSystem,
  type SystemCreateInput,
  type SystemDetail,
  type SystemUpdateInput,
} from '@/hooks/useSystems';
import { useZabbixHosts } from '@/hooks/useZabbixIntegration';
import { policyLabel, scopeLabel } from '@/lib/format';
import type { SystemScope, UpdatePolicy } from '@/types/api';

/**
 * System-Formular (Anlegen + Bearbeiten, Spec S8-06 §5).
 *
 * - customer nur beim Anlegen wählbar (danach fix).
 * - role/os_family/os_version: Freitext mit Vorschlägen aus GET
 *   /systems/facets (datalist, Combobox kann kein Freitext-Commit).
 * - ip_addresses als kommagetrennte Eingabe.
 * - Regel 2 der Spec: vpn_ref ist NUR ein Dateiname und
 *   credentials_available NUR ein Flag; bw_item_id ist NUR die zentrale
 *   Bitwarden-Item-Referenz (Lockerung per Entscheid 12.08.2026) — .ovpn-
 *   Dateien, Keys, bw CLI und BW_SESSION bleiben am Sysadmin-PC, hier wird
 *   nichts Geheimes gespeichert.
 * - Regel 1: managed/eligibility sind reine Metadaten — kein Feld hier
 *   koppelt Runs oder Systempflege an Verträge.
 */

const SCOPE_OPTIONS: readonly SystemScope[] = [
  'in_scope',
  'manual_only',
  'excluded',
  'eol',
  'offline',
  'external',
];

const POLICY_OPTIONS: readonly UpdatePolicy[] = ['auto', 'manual', 'never', 'coordinated'];

// Spiegel des Backend-Patterns (schemas/system.py) für Sofort-Feedback
// statt roher 422-Antwort.
const ROLE_PATTERN = /^[a-z0-9][a-z0-9_-]*$/;

// Spiegel von _BW_ITEM_ID_PATTERN (schemas/system.py): Hex + Bindestriche,
// 8-64 Zeichen — bewusst lockerer als striktes UUID-Format.
export const BW_ITEM_ID_PATTERN = /^[0-9a-fA-F][0-9a-fA-F-]{7,63}$/;

/**
 * Wofür gilt die Bitwarden-Referenz? (Migration 0028, Befund 20.08.2026)
 *
 * 'ssh' war bis dahin die einzige Lesart: der wt-client injizierte das
 * Passwort UND verbot dem Host die Key-Authentifizierung. An pve-ha01
 * (L-Quadrat) hing aber ein Weblogin der Proxmox-GUI (8006) — der Host wurde
 * in der Vorabprüfung grün gemeldet und starb danach in jedem einzelnen
 * ansible-Task an der Anmeldung. 'web' liefert das Passwort weiterhin (die
 * OPNsense-Routinen lesen es als GUI-Passwort), lässt die SSH-Argumente
 * aber in Ruhe.
 *
 * Aus dem generierten Schema abgeleitet, damit ein Spec-Umbau hier als
 * tsc-Fehler auffällt statt still zu driften.
 */
export type BwItemKind = SystemDetail['bw_item_kind'];

const BW_ITEM_KIND_OPTIONS: readonly { value: BwItemKind; label: string }[] = [
  { value: 'ssh', label: 'SSH-Passwort (Anmeldung am Host)' },
  { value: 'web', label: 'Weblogin (GUI, z.B. Proxmox 8006, PBS 8007, NPM 81)' },
];

/**
 * Service-Codes und OS-Familien, deren Zugang üblicherweise eine Web-GUI
 * ist. Bewusst nur ein HINWEIS und keine Automatik: dieselbe Maschine kann
 * per SSH-Passwort gewartet und über die GUI abgefragt werden, und die
 * Migration 0028 rät deshalb absichtlich nicht (Backfill nach 'web' wäre
 * der Rückfall hinter den Hasselbach-Fix vom 19.08.2026).
 */
export const WEB_GUI_HINTS: readonly string[] = [
  'opnsense',
  'pfsense',
  'proxmox-ve',
  'proxmox-bs',
  'proxmox-backup',
  'nginx-rproxy',
  'unifi',
  'plesk',
];

/**
 * Die os_family-Werte, die das Tool kennt und klassifiziert.
 *
 * Quelle: itkfm_os aus dem Wiki-Inventar, klassifiziert in
 * `app/services/ansible_runner/inventory.py` (LINUX_OS/WINDOWS_OS/FIREWALL_OS);
 * dieselbe Liste steht im Backend als `KNOWN_OS_FAMILIES` und im Routinen-Repo
 * in `scripts/pruefe_routinen.py`.
 *
 * WOZU DIE WARNUNG: `systems.os_family` ist ein freies Textfeld (max 64
 * Zeichen, keine Allowlist), und seit dem 10.09.2026 kann eine Routine allein
 * über diesen Wert an ein System gebunden sein. Wer hier "Debian 12" statt
 * "debian" einträgt, nimmt das System still aus jedem Linux-Rollout heraus -
 * in der Kundenvorschau steht dann bloß "kein passender Routinen-Typ".
 * Umgekehrt zieht eine Appliance, die fälschlich "debian" trägt, ein
 * Linux-Playbook auf sich.
 *
 * Bewusst nur ein Hinweis und keine Sperre: Ein neuer Inventarwert soll sich
 * eintragen lassen, ohne dass vorher jemand drei Repos anfasst.
 */
export const KNOWN_OS_FAMILIES: readonly string[] = [
  'centos',
  'debian',
  'freepbx',
  'fritzbox',
  'hyper-v',
  'linux',
  'opnsense',
  'pfsense',
  'proxmox-backup',
  'proxmox-ve',
  'raspbian',
  'sangoma',
  'synology',
  'ubuntu',
  'unbekannt',
  'unifi',
  'windows-client',
  'windows-server',
];

/** Hinweistext, wenn die eingetippte os_family keinem bekannten Wert entspricht. */
export function osFamilyHinweis(wert: string): string | undefined {
  const norm = wert.trim().toLowerCase();
  if (norm === '' || KNOWN_OS_FAMILIES.includes(norm)) return undefined;
  return (
    `"${wert.trim()}" ist keiner der bekannten Werte (${KNOWN_OS_FAMILIES.join(', ')}). ` +
    'Routinen, die über die Betriebssystem-Familie binden, erreichen dieses System dann nicht.'
  );
}

/** True, wenn Services/OS-Family dieses Systems nach einer Web-GUI aussehen. */
export function suggestsWebLogin(
  serviceTypeCodes: readonly string[] | null | undefined,
  osFamily: string | null | undefined
): boolean {
  const kandidaten = [...(serviceTypeCodes ?? []), osFamily ?? '']
    .map((c) => c.trim().toLowerCase())
    .filter((c) => c !== '');
  return kandidaten.some((c) => WEB_GUI_HINTS.includes(c));
}

/**
 * Live-Slugify der Rollen-Eingabe: lowercase, Leerzeichen -> '-', max 40.
 * Übrige unerlaubte Zeichen werden NICHT stumm entfernt, sondern über
 * ROLE_PATTERN als Eingabefehler angezeigt.
 */
export function slugifyRole(text: string): string {
  return text
    .toLowerCase()
    .replace(/^\s+/, '')
    .replace(/\s+/g, '-')
    .slice(0, 40);
}

/** Kommagetrennte Eingabe -> IP-Liste (auch Whitespace/Semikolon toleriert). */
export function parseIpAddresses(text: string): string[] {
  return text
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/**
 * Spiegelt safe_alias() aus generator.py. Füllt NUR den Platzhalter —
 * das Feld bleibt leer, sonst wäre der Alias festgeschrieben.
 */
export function safeAlias(v: string): string {
  return v.toLowerCase().replace(/[^a-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '') || 'host';
}

/**
 * Das Backend akzeptiert als Hypervisor auch ein System ohne Rolle, sofern
 * proxmox_node gesetzt ist (resolve_hypervisor in snapshot/service.py). Ein
 * aus dem Inventar importierter PVE-Host hat oft os_family='proxmox-ve' und
 * proxmox_node, aber eine leere Rolle — filtert man nur auf
 * role === 'hypervisor', taucht genau der Host nicht auf, für den das
 * Feld gebaut wird.
 */
export function istHypervisorKandidat(s: SystemDetail): boolean {
  return (
    s.role === 'hypervisor' ||
    s.os_family === 'proxmox-ve' ||
    Boolean(s.proxmox_node && s.proxmox_node.trim() !== '')
  );
}

interface FormState {
  customerId: string | null;
  locationId: string | null;
  displayName: string;
  hostname: string;
  fqdn: string;
  ipAddresses: string;
  role: string;
  osFamily: string;
  osVersion: string;
  scope: SystemScope;
  updatePolicy: UpdatePolicy;
  managed: boolean;
  isCritical: boolean;
  ansibleAlias: string;
  vpnRef: string;
  credentialsAvailable: boolean;
  bwItemId: string;
  bwItemKind: BwItemKind;
  /**
   * Zabbix-Host-Override (Spec S8-03). Eingabe-Konvention: leer = Auto-
   * Match (API null), '-' = vom Ingest ausschließen (API Leerstring),
   * sonst technischer Zabbix-Hostname (host.host).
   */
  zabbixHostRef: string;
  proxmoxNode: string;
  proxmoxVmid: string;
  parentSystemId: string | null;
  notes: string;
}

function emptyForm(customerId?: string): FormState {
  return {
    customerId: customerId ?? null,
    locationId: null,
    displayName: '',
    hostname: '',
    fqdn: '',
    ipAddresses: '',
    role: '',
    osFamily: '',
    osVersion: '',
    scope: 'in_scope',
    updatePolicy: 'auto',
    managed: false,
    isCritical: false,
    ansibleAlias: '',
    vpnRef: '',
    credentialsAvailable: false,
    bwItemId: '',
    bwItemKind: 'ssh',
    zabbixHostRef: '',
    proxmoxNode: '',
    proxmoxVmid: '',
    parentSystemId: null,
    notes: '',
  };
}

/** API-Wert -> Eingabefeld: null = '', Leerstring (Ausschluss) = '-'. */
function zabbixRefToInput(value: string | null | undefined): string {
  if (value == null) return '';
  if (value === '') return '-';
  return value;
}

/** Eingabefeld -> API-Wert: '' = null (Auto-Match), '-' = Leerstring. */
export function zabbixRefFromInput(text: string): string | null {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  if (trimmed === '-') return '';
  return trimmed;
}

function formFromSystem(system: SystemDetail): FormState {
  return {
    customerId: system.customer_id,
    locationId: system.location_id ?? null,
    displayName: system.display_name ?? '',
    hostname: system.hostname,
    fqdn: system.fqdn ?? '',
    ipAddresses: (system.ip_addresses ?? []).join(', '),
    role: system.role ?? '',
    osFamily: system.os_family ?? '',
    osVersion: system.os_version ?? '',
    scope: system.scope,
    updatePolicy: system.update_policy,
    managed: system.managed ?? false,
    isCritical: system.is_critical ?? false,
    ansibleAlias: system.ansible_alias ?? '',
    vpnRef: system.vpn_ref ?? '',
    credentialsAvailable: system.credentials_available ?? false,
    bwItemId: system.bw_item_id ?? '',
    // Alter Server ohne das Feld -> 'ssh' (bisheriges Verhalten).
    bwItemKind: system.bw_item_kind === 'web' ? 'web' : 'ssh',
    zabbixHostRef: zabbixRefToInput(system.zabbix_host_ref),
    proxmoxNode: system.proxmox_node ?? '',
    proxmoxVmid: system.proxmox_vmid != null ? String(system.proxmox_vmid) : '',
    parentSystemId: system.parent_system_id ?? null,
    notes: system.notes ?? '',
  };
}

export type SystemFormDialogState =
  | { mode: 'create'; customerId?: string }
  | { mode: 'edit'; system: SystemDetail };

interface SystemFormDialogProps {
  state: SystemFormDialogState | null;
  onClose: () => void;
  onSuccess?: (system: SystemDetail) => void;
}

export function SystemFormDialog({
  state,
  onClose,
  onSuccess,
}: SystemFormDialogProps): JSX.Element {
  const isEdit = state?.mode === 'edit';
  const roleListId = useId();
  const osFamilyListId = useId();
  const osVersionListId = useId();
  const nodeListId = useId();
  const fqdnListId = useId();
  const ipListId = useId();
  const vpnListId = useId();
  const zabbixListId = useId();
  const hostnameListId = useId();

  const createMutation = useCreateSystem();
  const updateMutation = useUpdateSystem();

  const [form, setForm] = useState<FormState>(emptyForm());
  // Nur der manuelle Zustand; aufgeklappt wird der Block außerdem, sobald er
  // eine Warnung oder einen Fehler trägt (siehe unten).
  const [erweitertOffen, setErweitertOffen] = useState(false);
  // Zuletzt aus dem Elternsystem vorgeschlagener Node; Abwahl leert das
  // Feld nur, wenn der Anwender den Wert nicht angefasst hat.
  const [nodeVorschlag, setNodeVorschlag] = useState<string | null>(null);
  // Was der letzte Klick auf „Aus Zabbix übernehmen" gefüllt hat — der
  // Anwender muss sehen, was ihm da ins Formular gelaufen ist.
  const [zabbixUebernommen, setZabbixUebernommen] = useState<string[] | null>(null);

  // Formular beim Öffnen mit Create-Defaults bzw. Edit-Werten befüllen.
  useEffect(() => {
    if (!state) return;
    createMutation.reset();
    updateMutation.reset();
    setForm(
      state.mode === 'edit' ? formFromSystem(state.system) : emptyForm(state.customerId)
    );
    setNodeVorschlag(null);
    setZabbixUebernommen(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  // Kunden-Auswahl nur beim Anlegen nötig; Slug brauchen wir auch im Edit
  // für den Alias-Platzhalter.
  const customersQuery = useCustomers({ limit: 200 });
  const locationsQuery = useCustomerLocations(form.customerId ?? undefined);
  const facetsQuery = useSystemFacets(form.customerId ?? undefined);
  const zabbixSuche = useDebouncedValue(form.zabbixHostRef.trim(), 300);
  const zabbixHostsQuery = useZabbixHosts({
    customerId: form.customerId ?? undefined,
    q: zabbixSuche,
    onlyUnassigned: true,
    limit: 50,
    enabled: Boolean(form.customerId) || isEdit,
  });
  // useMemo, sonst ist `?? []` jedes Render ein neues Array und die
  // Hostname-/Host-Vorschläge flackern in den exhaustive-deps.
  const zabbixHosts = useMemo(
    () => zabbixHostsQuery.data?.hosts ?? [],
    [zabbixHostsQuery.data?.hosts]
  );
  const zabbixKonfiguriert = zabbixHostsQuery.data?.configured ?? false;
  // Hoch angesetzt, weil Default 50 die Hypervisor-Kandidaten kappen kann.
  const siblingsQuery = useSystems({
    ...(form.customerId ? { customerId: form.customerId } : {}),
    limit: 500,
  });

  const customerOptions = (customersQuery.data?.data ?? []).map((c) => ({
    value: c.id,
    label: c.name,
    hint: c.slug,
  }));
  const locationOptions = (locationsQuery.data?.data ?? []).map((l) => ({
    value: l.id,
    label: l.name,
    ...(l.address ? { hint: l.address } : {}),
  }));
  const facets = facetsQuery.data;
  const roleSuggestions = facets?.roles ?? [];
  const osFamilySuggestions = facets?.os_families ?? [];
  const osVersionSuggestions = useMemo(
    () => osVersionVorschlaege(facets?.os_versions, form.osFamily),
    [facets?.os_versions, form.osFamily]
  );

  const kundenSysteme = form.customerId ? (siblingsQuery.data?.data ?? []) : [];
  const eigenId = state?.mode === 'edit' ? state.system.id : null;
  const parentOptions = useMemo(() => {
    const seen = new Set<string>();
    const kandidaten: SystemDetail[] = [];
    for (const s of kundenSysteme) {
      if (!istHypervisorKandidat(s) || s.id === eigenId || seen.has(s.id)) continue;
      seen.add(s.id);
      kandidaten.push(s);
    }
    return kandidaten
      .sort((a, b) =>
        (a.display_name || a.hostname).localeCompare(b.display_name || b.hostname, 'de')
      )
      .map((s) => ({
        value: s.id,
        label: s.display_name?.trim() ? s.display_name : s.hostname,
        ...(s.proxmox_node?.trim() ? { hint: s.proxmox_node.trim() } : {}),
      }));
  }, [kundenSysteme, eigenId]);

  const nodeDatalist = useMemo(() => {
    const gesehen = new Set<string>();
    const vonKunden: string[] = [];
    for (const s of kundenSysteme) {
      const n = s.proxmox_node?.trim();
      if (n && !gesehen.has(n)) {
        gesehen.add(n);
        vonKunden.push(n);
      }
    }
    vonKunden.sort((a, b) => a.localeCompare(b, 'de'));
    const extra: string[] = [];
    for (const f of facets?.proxmox_nodes ?? []) {
      const n = f.value.trim();
      if (!n || gesehen.has(n)) continue;
      gesehen.add(n);
      extra.push(n);
    }
    return [...vonKunden, ...extra].slice(0, 40);
  }, [kundenSysteme, facets?.proxmox_nodes]);

  const domainVorschlaege = useMemo(
    () => vorschlagsWerte(facets?.domains ?? []).map((d) => d.value),
    [facets?.domains]
  );
  const fqdnListe = useMemo(
    () => fqdnVorschlaege(form.hostname, domainVorschlaege),
    [form.hostname, domainVorschlaege]
  );
  const vpnVorschlaege = useMemo(
    () => vorschlagsWerte(facets?.vpn_refs ?? []),
    [facets?.vpn_refs]
  );
  const gewaehlterZabbixHost = useMemo(
    () => zabbixHosts.find((h) => h.host === form.zabbixHostRef.trim()) ?? null,
    [zabbixHosts, form.zabbixHostRef]
  );
  const hostnameVorschlaege = useMemo(() => {
    const gesehen = new Set<string>();
    const liste: { hostid: string; kurz: string; label: string }[] = [];
    for (const h of zabbixHosts) {
      const kurz = h.host.split('.')[0]?.trim() ?? '';
      if (kurz === '' || gesehen.has(kurz)) continue;
      gesehen.add(kurz);
      liste.push({ hostid: h.hostid, kurz, label: zabbixHostLabel(h) });
    }
    return liste;
  }, [zabbixHosts]);
  const zabbixIpOptionen = useMemo(() => {
    if (!gewaehlterZabbixHost) return { alle: '', einzeln: [] as string[] };
    const gesehen = new Set<string>();
    const einzeln: string[] = [];
    for (const ip of gewaehlterZabbixHost.ips) {
      const t = ip.trim();
      if (t === '' || gesehen.has(t)) continue;
      gesehen.add(t);
      einzeln.push(t);
    }
    return { alle: einzeln.join(', '), einzeln };
  }, [gewaehlterZabbixHost]);

  const zabbixHinweis = ((): string => {
    if (!isEdit && !form.customerId) return 'Erst Kunde wählen.';
    if (zabbixHostsQuery.isError) {
      return 'Zabbix ist gerade nicht erreichbar — der Hostname lässt sich trotzdem von Hand eintragen.';
    }
    if (!zabbixKonfiguriert && !zabbixHostsQuery.isPending) {
      return 'Zabbix ist nicht konfiguriert; das Feld bleibt ein Freitext-Override.';
    }
    if (isEdit) {
      return "Technischer Zabbix-Hostname (host.host). Leer = automatisch zuordnen (Hostname/FQDN/IP), '-' = System vom Zabbix-Ingest ausschließen.";
    }
    return 'Host wählen, dann „Aus Zabbix übernehmen" — das füllt Hostname, FQDN, IP-Adressen und Betriebssystem. Nur leere Felder werden gefüllt.';
  })();

  const onZabbixUebernehmen = (): void => {
    if (!gewaehlterZabbixHost) return;
    // Berechnung vor setForm, damit StrictMode die Meldung nicht doppelt setzt.
    const { werte, uebernommen } = zabbixUebernahme(
      {
        hostname: form.hostname,
        fqdn: form.fqdn,
        ipAddresses: form.ipAddresses,
        osFamily: form.osFamily,
        osVersion: form.osVersion,
      },
      gewaehlterZabbixHost
    );
    setZabbixUebernommen(uebernommen);
    setForm((f) => ({ ...f, ...werte }));
  };

  const gewaehltesEltern = kundenSysteme.find((s) => s.id === form.parentSystemId) ?? null;

  const kundenSlug =
    (customersQuery.data?.data ?? []).find((c) => c.id === form.customerId)?.slug ?? '';
  const aliasPlatzhalter = `${safeAlias(kundenSlug)}__${safeAlias(form.displayName || form.hostname)}`;

  const bestandVmid =
    state?.mode === 'edit' && state.system.proxmox_vmid != null
      ? String(state.system.proxmox_vmid)
      : '';

  // Sichtbarkeit hängt am GESPEICHERTEN Wert, nicht am Eingabefeld: sonst
  // verschwindet das Feld unter der Hand, sobald der Anwender die letzte
  // Ziffer löscht - und sein bewusstes Leeren käme nie beim Server an
  // (der Absende-Zweig unten hängt an vmidSichtbar).
  const vmidSichtbar =
    form.parentSystemId !== null ||
    form.role === 'hypervisor' ||
    bestandVmid !== '';

  const onParentChange = (parentId: string | null): void => {
    if (parentId === null) {
      // Ohne Eltern-ID, mit gesetztem proxmox_node und ohne Rolle hält
      // resolve_hypervisor die VM für ihren eigenen Hypervisor — die
      // PVE-API würde dann gegen die VM selbst gerichtet.
      setForm((f) => {
        const unveraendert =
          nodeVorschlag !== null && f.proxmoxNode.trim() === nodeVorschlag;
        return {
          ...f,
          parentSystemId: null,
          proxmoxNode: unveraendert ? '' : f.proxmoxNode,
        };
      });
      setNodeVorschlag(null);
      return;
    }
    const parent = kundenSysteme.find((s) => s.id === parentId);
    // Nur der echte PVE-Node-Name darf hier landen. Der Hostname des
    // PVE-Systems ist NICHT der Node-Name (z.B. Host
    // 'pve-hasselbach01.kunde.local' vs. Node 'pve01'). Würde man ihn
    // eintragen, gewänne dieser falsche String gegen den funktionierenden
    // Backend-Fallback (snapshot/service.py: node = system.proxmox_node or
    // hv.proxmox_node) und die PVE-API kennt den Node nicht -> Pre-Snapshot
    // scheitert, Echtlauf wird abgebrochen. Leer lassen ist richtig.
    const node = parent?.proxmox_node?.trim() ?? '';
    const aktuellLeer = form.proxmoxNode.trim() === '';
    const vorschlagen = Boolean(node) && aktuellLeer;
    setForm((f) => ({
      ...f,
      parentSystemId: parentId,
      proxmoxNode: vorschlagen ? node : f.proxmoxNode,
    }));
    setNodeVorschlag(vorschlagen ? node : nodeVorschlag);
  };

  const hostnameMissing = form.hostname.trim() === '';
  const customerMissing = !isEdit && !form.customerId;
  const roleInvalid = form.role !== '' && !ROLE_PATTERN.test(form.role);
  const vmidTrimmed = form.proxmoxVmid.trim();
  const vmidInvalid =
    vmidTrimmed !== '' && (!/^\d+$/.test(vmidTrimmed) || Number(vmidTrimmed) < 1);
  const bwItemIdTrimmed = form.bwItemId.trim();
  const bwItemIdInvalid = bwItemIdTrimmed !== '' && !BW_ITEM_ID_PATTERN.test(bwItemIdTrimmed);
  // Nur ein Hinweis, keine Automatik: Migration 0028 rät bewusst nicht (ein
  // Backfill nach 'web' wäre der Rückfall hinter den Hasselbach-Fix vom
  // 19.08.2026), also muss die Kandidatenliste hier sichtbar werden.
  const webLoginVermutet = suggestsWebLogin(
    state?.mode === 'edit' ? state.system.service_type_codes : undefined,
    form.osFamily
  );
  // Dasselbe Muster wie am Kundenfeld: nur der Dateiname, kein Pfad. Ohne die
  // Prüfung nahm das Backend "/etc/openvpn/kunde.ovpn" an (max_length=200,
  // kein Muster) und der Client lehnte es nachts ab - der Lauf fiel auf diesem
  // System durch (Befund 10.09.2026).
  const vpnRefTrimmed = form.vpnRef.trim();
  const vpnRefInvalid = vpnRefTrimmed !== '' && !VPN_PROFIL_MUSTER.test(vpnRefTrimmed);
  // Welcher Kundenstandard gilt hier eigentlich? Stand nirgends - wer es
  // wissen wollte, musste den Bearbeiten-Dialog des Kunden öffnen.
  // Der Hoster als Anzeigename; unbekannte Id fällt auf die Id zurück,
  // damit der Wert nicht lautlos verschwindet.
  const hostedById = state?.mode === 'edit' ? (state.system.hosted_by_customer_id ?? null) : null;
  const gehostetBei =
    hostedById === null
      ? null
      : ((customersQuery.data?.data ?? []).find((c) => c.id === hostedById)?.name ?? hostedById);
  // Der Standard, der für DIESES System gilt, kommt vom NETZ-Eigentümer:
  // bei einem gehosteten System ist das der Hoster, sonst der Vertragskunde
  // (genau die Kette, die inventory.py für itkfm_vpn geht). Vorher stand
  // hier immer der Kundenstandard - direkt unter dem Satz, dass Netz und VPN
  // dem Hoster folgen, und damit die falsche Antwort auf genau die Frage,
  // die der Hinweis beantworten soll (Befund 10.09.2026, zweite Runde).
  const netzEigentuemer =
    hostedById === null
      ? (customersQuery.data?.data ?? []).find((c) => c.id === form.customerId)
      : (customersQuery.data?.data ?? []).find((c) => c.id === hostedById);
  // Steht der Hoster nicht in der Liste, nennen wir lieber gar keinen Wert
  // als den des Vertragspartners.
  const netzEigentuemerBekannt = hostedById === null || netzEigentuemer !== undefined;
  const kundenVpnProfil = netzEigentuemer?.vpn_profile ?? null;
  // Der Block klappt sich selbst auf, sobald darin etwas zu sehen ist. Vorher
  // steckten die Warnung "bitte auf Weblogin stellen" und der Grund für ein
  // dauerhaft ausgegrautes "Speichern" in einem zugeklappten <details> - und
  // niemand klappt auf, was er nicht sucht.
  const erweitertHatWarnung = webLoginVermutet && form.bwItemKind === 'ssh';
  const erweitertHatFehler = bwItemIdInvalid || vpnRefInvalid;
  const erweitertAnzahl = (erweitertHatWarnung ? 1 : 0) + (bwItemIdInvalid ? 1 : 0) + (vpnRefInvalid ? 1 : 0);

  const saving = createMutation.isPending || updateMutation.isPending;
  const error = isEdit ? updateMutation.error : createMutation.error;

  const submit = (): void => {
    if (
      !state ||
      hostnameMissing ||
      customerMissing ||
      roleInvalid ||
      vmidInvalid ||
      bwItemIdInvalid
    )
      return;
    const t = (v: string): string | null => {
      const trimmed = v.trim();
      return trimmed === '' ? null : trimmed;
    };
    const shared: SystemUpdateInput = {
      location_id: form.locationId,
      hostname: form.hostname.trim(),
      display_name: t(form.displayName),
      fqdn: t(form.fqdn),
      ip_addresses: parseIpAddresses(form.ipAddresses),
      // Bereits per slugifyRole normalisiert (lowercase, '-'-Slug).
      role: t(form.role),
      os_family: t(form.osFamily),
      os_version: t(form.osVersion),
      scope: form.scope,
      update_policy: form.updatePolicy,
      managed: form.managed,
      is_critical: form.isCritical,
      ansible_alias: t(form.ansibleAlias),
      vpn_ref: t(form.vpnRef),
      credentials_available: form.credentialsAvailable,
      // Nur die Referenz (Regel-2-Lockerung 12.08.2026); leer = entfernen.
      bw_item_id: t(form.bwItemId),
      // Wofür die Referenz gilt (Migration 0028). Wird immer mitgeschickt,
      // auch ohne Item-ID: die Kennzeichnung soll erhalten bleiben, wenn die
      // ID später wieder eingetragen wird.
      bw_item_kind: form.bwItemKind,
      proxmox_node: t(form.proxmoxNode),
      parent_system_id: form.parentSystemId,
      // Ausgeblendete Felder nullen nichts: SystemUpdate hat
      // exclude_unset-Semantik, ein explizites null LÖSCHT die VMID. Ein
      // Bestandssystem mit proxmox_vmid=137 und ohne Elternsystem würde
      // sonst bei jedem Speichern (auch nur einer Notizänderung) still
      // seine VMID verlieren - und der nächste Echtlauf bricht ab
      // ("System X hat keine proxmox_vmid").
      // Unveränderter Bestand ohne Hypervisor-Kontext: Schlüssel weglassen,
      // obwohl das Feld sichtbar bleibt (sonst verschwindet der Wert aus
      // dem Blickfeld). Bewusstes Aendern/Leeren sendet den neuen Wert.
      ...(vmidSichtbar &&
      (form.parentSystemId !== null ||
        form.role === 'hypervisor' ||
        form.proxmoxVmid.trim() !== bestandVmid)
        ? { proxmox_vmid: vmidTrimmed === '' ? null : Number(vmidTrimmed) }
        : {}),
      notes: t(form.notes),
    };
    const handleSuccess = (result: SystemDetail): void => {
      onSuccess?.(result);
      onClose();
    };
    if (state.mode === 'edit') {
      // zabbix_host_ref nur im Edit-PATCH (Create ignoriert das Feld
      // serverseitig): '' -> null (Auto-Match), '-' -> Leerstring (Ausschluss).
      const body: SystemUpdateInput = {
        ...shared,
        zabbix_host_ref: zabbixRefFromInput(form.zabbixHostRef),
      };
      updateMutation.mutate(
        { systemId: state.system.id, body },
        { onSuccess: handleSuccess }
      );
    } else {
      const body: SystemCreateInput = {
        ...shared,
        customer_id: form.customerId ?? '',
        hostname: form.hostname.trim(),
        // Seit 11.09.2026 kennt auch POST /systems das Feld. Ohne diese Zeile
        // ging die gerade getroffene Host-Auswahl beim Speichern verloren und
        // das neue System stand sofort als unmatched im Zabbix-Report.
        zabbix_host_ref: zabbixRefFromInput(form.zabbixHostRef),
      };
      createMutation.mutate(body, { onSuccess: handleSuccess });
    }
  };

  return (
    <Dialog
      open={Boolean(state)}
      onClose={onClose}
      title={isEdit ? 'System bearbeiten' : 'Neues System'}
      description={
        isEdit
          ? 'Änderungen gelten sofort für Listen, Inventar-Export und kommende Runs.'
          : 'System manuell anlegen — unabhängig von Verträgen voll wartbar.'
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button
            onClick={submit}
            loading={saving}
            disabled={
              hostnameMissing ||
              customerMissing ||
              roleInvalid ||
              vmidInvalid ||
              bwItemIdInvalid ||
              vpnRefInvalid
            }
            data-testid="system-save"
          >
            Speichern
          </Button>
        </>
      }
    >
      <div className="flex max-h-[65vh] flex-col gap-3 overflow-y-auto pr-1">
        {/* Kunden-Query ist Pflicht-Datenquelle beim Anlegen -> Fehler sichtbar
            machen; facetsQuery/locationsQuery dürfen still degradieren
            (Vorschlaege/optionale Zuordnung). */}
        {!isEdit && customersQuery.isError && <ErrorAlert error={customersQuery.error} />}
        {!isEdit && (
          <Combobox
            label="Kunde"
            value={form.customerId}
            onChange={(v) => {
              setForm((f) => ({
                ...f,
                customerId: v,
                locationId: null,
                parentSystemId: null,
              }));
              setNodeVorschlag(null);
            }}
            options={customerOptions}
            required
            disabled={customersQuery.isLoading}
            placeholder={
              customersQuery.isLoading ? 'Kunden werden geladen ...' : 'Kunde wählen ...'
            }
          />
        )}
        <Combobox
          label="Standort"
          value={form.locationId}
          onChange={(v) => setForm((f) => ({ ...f, locationId: v }))}
          options={locationOptions}
          disabled={!form.customerId || locationOptions.length === 0}
          placeholder={
            form.customerId ? 'Kein Standort zugeordnet' : 'Erst Kunde wählen'
          }
        />
        <div data-testid="system-zabbix-block">
          <Input
            label="Zabbix-Host"
            value={form.zabbixHostRef}
            onChange={(e) => {
              const wert = e.target.value;
              setForm((f) => ({ ...f, zabbixHostRef: wert }));
              setZabbixUebernommen(null);
            }}
            list={zabbixListId}
            placeholder="host.kunde.at"
            hint={zabbixHinweis}
            className="font-mono"
            disabled={!isEdit && !form.customerId}
            data-testid="system-zabbix-host-ref"
          />
          <datalist id={zabbixListId}>
            {zabbixHosts.map((h) => (
              <option key={h.hostid} value={h.host}>
                {zabbixHostLabel(h)}
              </option>
            ))}
          </datalist>
          {gewaehlterZabbixHost && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              data-testid="system-zabbix-uebernehmen"
              onClick={onZabbixUebernehmen}
            >
              Aus Zabbix übernehmen
            </Button>
          )}
          {zabbixUebernommen !== null && (
            <p className="mt-1 text-xs text-muted" data-testid="system-zabbix-uebernommen">
              {zabbixUebernommen.length === 0
                ? 'Nichts übernommen — alle betroffenen Felder waren schon gefüllt.'
                : `Übernommen: ${zabbixUebernommen.join(', ')}.`}
            </p>
          )}
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input
            label="Name"
            value={form.displayName}
            onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
            hint="Anzeigename; leer = Hostname."
          />
          <div>
            <Input
              label="Hostname"
              value={form.hostname}
              onChange={(e) => setForm((f) => ({ ...f, hostname: e.target.value }))}
              list={hostnameListId}
              error={hostnameMissing ? 'Pflichtfeld.' : undefined}
              hint="Nur der kurze Name, ohne Domain."
              className="font-mono"
              data-testid="system-hostname"
            />
            <datalist id={hostnameListId}>
              {hostnameVorschlaege.map((h) => (
                <option key={h.hostid} value={h.kurz}>
                  {h.label}
                </option>
              ))}
            </datalist>
            {form.hostname.includes('.') && (
              <div className="mt-1 flex flex-col gap-1">
                <p className="text-xs text-muted">
                  {form.fqdn.trim() === ''
                    ? 'Der Hostname enthält einen Punkt. Als FQDN übernehmen und den Kurznamen behalten?'
                    : 'Der Hostname enthält einen Punkt. FQDN ist bereits gesetzt und wird nicht überschrieben; der Button setzt nur den Kurznamen.'}
                </p>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  data-testid="system-hostname-fqdn-vorschlag"
                  onClick={() =>
                    setForm((f) => {
                      const voll = f.hostname;
                      const kurz = voll.slice(0, voll.indexOf('.'));
                      return {
                        ...f,
                        hostname: kurz,
                        fqdn: f.fqdn.trim() === '' ? voll : f.fqdn,
                      };
                    })
                  }
                >
                  Als FQDN übernehmen
                </Button>
              </div>
            )}
          </div>
        </div>
        <div>
          <Input
            label="FQDN"
            value={form.fqdn}
            onChange={(e) => setForm((f) => ({ ...f, fqdn: e.target.value }))}
            list={fqdnListId}
            placeholder="host.kunde.at"
            hint="Optional. Voller Name inkl. Domain; zweiter Join-Schlüssel für die lokale bw-map."
            className="font-mono"
            data-testid="system-fqdn"
          />
          <datalist id={fqdnListId}>
            {fqdnListe.map((v) => (
              <option key={v} value={v} />
            ))}
          </datalist>
        </div>
        <div>
          <Input
            label="IP-Adressen"
            value={form.ipAddresses}
            onChange={(e) => setForm((f) => ({ ...f, ipAddresses: e.target.value }))}
            list={ipListId}
            placeholder="10.0.0.10, 192.168.1.1"
            hint="Kommagetrennt."
            className="font-mono"
            data-testid="system-ips"
          />
          <datalist id={ipListId}>
            {zabbixIpOptionen.alle !== '' && (
              <option value={zabbixIpOptionen.alle}>Alle IPs des Zabbix-Hosts</option>
            )}
            {zabbixIpOptionen.einzeln.map((ip) => (
              <option key={ip} value={ip} />
            ))}
          </datalist>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <Input
              label="Rolle"
              value={form.role}
              onChange={(e) => setForm((f) => ({ ...f, role: slugifyRole(e.target.value) }))}
              list={roleListId}
              placeholder="z.B. fileserver"
              hint="Freitext; Vorschläge aus vorhandenen Rollen."
              error={
                roleInvalid
                  ? 'Nur Kleinbuchstaben, Ziffern, - und _ (Beginn: Buchstabe/Ziffer).'
                  : undefined
              }
              data-testid="system-role"
            />
            <datalist id={roleListId}>
              {roleSuggestions.map((r) => (
                <option key={r.value} value={r.value}>
                  {`${r.value} (${r.count})`}
                </option>
              ))}
            </datalist>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Input
                /* Ein Feld, ein Name: der Wizard führte "OS", die
                   Routinen-Liste "OS: debian", dieses Formular "OS-Family" und
                   sein eigener Warntext "Betriebssystem-Familie" - alles
                   systems.os_family, und genau dieses Feld entscheidet seit
                   dem 10.09.2026, ob eine Routine ein System trifft. */
                label="Betriebssystem (Familie)"
                value={form.osFamily}
                onChange={(e) =>
                  setForm((f) => ({ ...f, osFamily: e.target.value.toLowerCase() }))
                }
                list={osFamilyListId}
                placeholder="debian"
                hint={
                  osFamilyHinweis(form.osFamily) ??
                  'Freitext; steuert Ansible-Gruppen und Shell-Typ — und seit 10.09.2026 auch, welche Routinen dieses System erreichen.'
                }
                data-testid="system-os-family"
              />
              <datalist id={osFamilyListId}>
                {osFamilySuggestions.map((r) => (
                  <option key={r.value} value={r.value}>
                    {`${r.value} (${r.count})`}
                  </option>
                ))}
              </datalist>
            </div>
            <div>
              <Input
                label="Betriebssystem (Version)"
                value={form.osVersion}
                onChange={(e) => setForm((f) => ({ ...f, osVersion: e.target.value }))}
                list={osVersionListId}
                placeholder="12"
                data-testid="system-os-version"
              />
              <datalist id={osVersionListId}>
                {osVersionSuggestions.map((r) => (
                  <option key={r.value} value={r.value}>
                    {`${r.value} (${r.count})`}
                  </option>
                ))}
              </datalist>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">Eligibility (Scope)</span>
            <select
              value={form.scope}
              onChange={(e) => setForm((f) => ({ ...f, scope: e.target.value as SystemScope }))}
              className="rounded-md border border-border bg-surface px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              data-testid="system-scope"
            >
              {SCOPE_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {scopeLabel(s)}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">Update-Policy</span>
            <select
              value={form.updatePolicy}
              onChange={(e) =>
                setForm((f) => ({ ...f, updatePolicy: e.target.value as UpdatePolicy }))
              }
              className="rounded-md border border-border bg-surface px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              {POLICY_OPTIONS.map((p) => (
                <option key={p} value={p}>
                  {policyLabel(p)}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.managed}
              onChange={(e) => setForm((f) => ({ ...f, managed: e.target.checked }))}
              className="size-4 rounded border-border"
              data-testid="system-managed"
            />
            <span>Verwaltet (managed)</span>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.isCritical}
              onChange={(e) => setForm((f) => ({ ...f, isCritical: e.target.checked }))}
              className="size-4 rounded border-border"
            />
            <span>Kritisch</span>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.credentialsAvailable}
              onChange={(e) =>
                setForm((f) => ({ ...f, credentialsAvailable: e.target.checked }))
              }
              className="size-4 rounded border-border"
            />
            <span>Zugangsdaten lokal vorhanden</span>
          </label>
        </div>
        <div data-testid="system-parent">
          <Combobox
            label="Elternsystem (Hypervisor)"
            value={form.parentSystemId}
            onChange={onParentChange}
            options={parentOptions}
            disabled={!form.customerId}
            placeholder={
              form.customerId ? 'Kein Elternsystem' : 'Erst Kunde wählen'
            }
          />
        </div>
        {form.proxmoxNode.trim() !== '' &&
          form.parentSystemId === null &&
          form.role !== 'hypervisor' && (
            <p
              className="text-xs text-warning"
              data-testid="system-node-ohne-eltern-warnung"
            >
              Ohne Elternsystem gilt dieses System mit gesetztem Proxmox-Node selbst als
              Hypervisor.
            </p>
          )}
        <div className={vmidSichtbar ? 'grid grid-cols-2 gap-3' : ''}>
          <div>
            <Input
              label="Proxmox-Node"
              value={form.proxmoxNode}
              onChange={(e) => setForm((f) => ({ ...f, proxmoxNode: e.target.value }))}
              list={nodeListId}
              hint={
                gewaehltesEltern && !gewaehltesEltern.proxmox_node?.trim()
                  ? `Name des PVE-Nodes (nicht der Hostname des PVE-Systems). Elternsystem-Hostname: ${gewaehltesEltern.hostname}`
                  : 'Name des PVE-Nodes (nicht der Hostname des PVE-Systems).'
              }
              className="font-mono"
            />
            <datalist id={nodeListId}>
              {nodeDatalist.map((n) => (
                <option key={n} value={n} />
              ))}
            </datalist>
          </div>
          {vmidSichtbar && (
            <Input
              label="Proxmox-VMID"
              inputMode="numeric"
              value={form.proxmoxVmid}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  proxmoxVmid: e.target.value.replace(/\D/g, ''),
                }))
              }
              error={vmidInvalid ? 'VMID muss eine Zahl >= 1 sein.' : undefined}
              data-testid="system-vmid"
            />
          )}
        </div>
        <details
          className="rounded-md border border-border"
          data-testid="system-erweitert"
          open={erweitertOffen || erweitertHatFehler || erweitertHatWarnung}
          onToggle={(e) => setErweitertOffen((e.target as HTMLDetailsElement).open)}
        >
          <summary className="cursor-pointer px-3 py-2 text-sm font-medium">
            <span className="inline-flex items-center gap-2">
              Erweitert (Ansible-Alias, VPN-Profil, Bitwarden)
              {erweitertAnzahl > 0 && (
                <span
                  className={
                    erweitertHatFehler
                      ? 'rounded-full bg-danger px-2 py-0.5 text-xs font-medium text-white'
                      : 'rounded-full bg-warning px-2 py-0.5 text-xs font-medium text-white'
                  }
                  data-testid="system-erweitert-badge"
                >
                  {erweitertAnzahl}
                </span>
              )}
            </span>
          </summary>
          <div className="flex flex-col gap-3 px-3 pb-3">
            <Input
              label="Ansible-Alias"
              value={form.ansibleAlias}
              onChange={(e) => setForm((f) => ({ ...f, ansibleAlias: e.target.value }))}
              placeholder={aliasPlatzhalter}
              hint="Leer = automatisch abgeleitet. Ein später geänderter Alias entwertet still die lokale bw-map-Zeile am Sysadmin-PC."
              className="font-mono"
            />
            {/* Nur lesbar: gesetzt wird die Hosting-Angabe über "System
                verschieben", weil Hosting und Vertragspartner dort gemeinsam
                entschieden werden. Sichtbar muss sie trotzdem sein - bisher
                war der Wert nach dem Setzen nirgends zu sehen und nur über
                ein erneutes Verschieben änderbar (Befund 10.09.2026). */}
            {gehostetBei !== null && (
              <p className="text-sm" data-testid="system-hosted-by">
                <span className="text-muted">Steht bei: </span>
                <span className="font-medium">{gehostetBei}</span>
                <span className="text-muted">
                  {' '}
                  — Netz und VPN-Profil dieses Systems folgen diesem Kunden, nicht dem
                  Vertragspartner. Ändern über „System verschieben“.
                </span>
              </p>
            )}
            <Input
              label="VPN-Profil (Override)"
              value={form.vpnRef}
              onChange={(e) => setForm((f) => ({ ...f, vpnRef: e.target.value }))}
              list={vpnListId}
              placeholder="kunde.ovpn"
              /* Der Satz über den Kundenstandard, der ALLE Systeme trifft,
                 gehört ans Kundenfeld. Hier steht, was für DIESES System
                 gilt - und zwar mit dem konkreten Wert. */
              hint={
                !netzEigentuemerBekannt
                  ? 'Leer = Standard des Hosters — der ist hier nicht geladen, also steht der Wert im Kunden-Dialog des Hosters.'
                  : kundenVpnProfil
                    ? hostedById === null
                      ? `Leer = Kundenstandard (derzeit: ${kundenVpnProfil}).`
                      : `Leer = Standard des Hosters ${gehostetBei ?? ''} (derzeit: ${kundenVpnProfil}).`
                    : hostedById === null
                      ? 'Leer = Kundenstandard — für diesen Kunden ist keiner gesetzt, dann baut der Client für dieses System keinen Tunnel auf.'
                      : `Leer = Standard des Hosters ${gehostetBei ?? ''} — dort ist keiner gesetzt, dann bekommt dieses System nachts keinen Tunnel.`
              }
              error={vpnRefInvalid ? VPN_PROFIL_FEHLER : undefined}
              className="font-mono"
              data-testid="system-vpn-ref"
            />
            <datalist id={vpnListId}>
              {vpnVorschlaege.map((v) => (
                <option key={v.value} value={v.value}>
                  {v.customer
                    ? `${v.value} — bei diesem Kunden (${v.count})`
                    : `${v.value} (${v.count})`}
                </option>
              ))}
            </datalist>
            <Input
              label="Bitwarden-Item-ID"
              value={form.bwItemId}
              onChange={(e) => setForm((f) => ({ ...f, bwItemId: e.target.value }))}
              placeholder="49c9bd44-4712-4e0f-a2f1-0a2c62f9b3d1"
              hint="Nur Referenz — Secrets bleiben in Bitwarden am Sysadmin-PC. ID steht im Web-Vault in der Adresszeile (…/#/vault?itemId=…), sobald der Eintrag offen ist."
              error={
                bwItemIdInvalid
                  ? 'Hex-Zeichen und Bindestriche, 8-64 Zeichen (Beginn: Hex-Zeichen).'
                  : undefined
              }
              className="font-mono"
              data-testid="system-bw-item-id"
            />
            {/* Wofür gilt der Tresor-Eintrag? Ohne diese Angabe hielt der
                wt-client jede Referenz für ein SSH-Passwort und schaltete die
                Key-Authentifizierung ab — an pve-ha01 (L-Quadrat) hing aber ein
                Weblogin der Proxmox-GUI (Befund 20.08.2026). */}
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium">Art des Bitwarden-Eintrags</span>
              <select
                value={form.bwItemKind}
                onChange={(e) =>
                  setForm((f) => ({ ...f, bwItemKind: e.target.value as BwItemKind }))
                }
                className="rounded-md border border-border bg-surface px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                data-testid="system-bw-item-kind"
              >
                {BW_ITEM_KIND_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <span className="text-xs text-muted">
                Bei „SSH-Passwort“ meldet sich der Client mit diesem Passwort am Host an und
                schaltet die Key-Anmeldung ab. Bei „Weblogin“ bleibt die Key-Anmeldung
                unangetastet — das Passwort geht nur an Routinen, die eine GUI abfragen.
              </span>
              {webLoginVermutet && form.bwItemKind === 'ssh' && (
                <span
                  className="text-xs text-warning"
                  data-testid="system-bw-item-kind-hinweis"
                >
                  Dieses System sieht nach einer Web-GUI aus (Proxmox, PBS, OPNsense &amp;
                  Co.). Falls der Tresor-Eintrag der GUI-Login ist, bitte auf „Weblogin“
                  stellen — sonst verbietet der Client dem Host die Key-Anmeldung.
                </span>
              )}
            </label>
          </div>
        </details>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">Notizen</span>
          <textarea
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            rows={3}
            className="rounded-md border border-border bg-surface px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          />
        </label>
        {error && <ErrorAlert error={error} />}
      </div>
    </Dialog>
  );
}
